import React, { useState, useCallback, useMemo, useRef, useEffect } from 'react';
import IconSystem from 'icon-collection/icon-systems';
import { ConfirmDialog } from 'components/modals/common/ConfirmDialog';
import { useModalMinimize } from 'contexts/ModalMinimizeContext';
import { SyutSelect } from 'components/ui/SyutSelect';
import notificationService from 'services/notificationService';
import { formatBackendTimestamps } from 'utils/errorHandling';
import { corporateService } from 'api/services/corporateService';
import type { Corporate, CreateCorporateRequest, UpdateCorporateRequest } from 'api/types';
import type { SelectOption } from 'components/ui/SyutSelect';
import PartyBillingSection, { type PartyBillingValues } from 'components/common/billing/PartyBillingSection';
import StateSelect from 'components/common/billing/StateSelect';
import { findState, usesStatePicker } from 'components/common/billing/taxAddress';
import SectionedModalShell, { ShellBlockers, type ShellSection } from 'components/modals/common/SectionedModalShell';
import { useTaxPack } from 'hooks/api/useTaxPack';
import type { SettlementMode } from 'api/types/settlement.types';

// ============================================================================
// TYPE DEFINITIONS
// ============================================================================

interface CorporateModalProps {
  isOpen: boolean;
  mode: 'create' | 'edit' | 'copy';
  record?: Corporate | null;
  onSave: () => void;
  onClose: () => void;
  // Minimize-to-bar support
  tabId?: string;
  moduleName?: string;
  onReopen?: () => void;
  createInstanceId?: string;
  copyInstanceId?: string;
  minimizeRef?: React.RefObject<(() => void) | null>;
  onRestoreModal?: (mode: 'create' | 'edit' | 'copy', instanceId?: string, corporateCode?: string) => void;
}

interface CorporateFormData {
  corporateCode: string;
  companyName: string;
  industry: string;
  contactPerson: string;
  designation: string;
  phone: string;
  email: string;
  website: string;
  billingAddress: string;
  billingCity: string;
  billingState: string;
  billingPincode: string;
  gstNumber: string;
  panNumber: string;
  creditDays: string;
  creditLimit: string;
  agreementStart: string;
  agreementEnd: string;
  discountType: string;
  discountValue: string;
  includeTaxes: boolean;
  isCreditEligible: boolean;
  isActive: boolean;
  gstLegalName: string;
  defaultSettlementMode: SettlementMode;
  showRatesOnGuestStatement: boolean;
  earlyDepartureRetention: string;
  isGstRegistered: boolean;
  defaultSupplyType: string;
}

// ============================================================================
// CONSTANTS
// ============================================================================

const INITIAL_FORM_DATA: CorporateFormData = {
  corporateCode: '',
  companyName: '',
  industry: '',
  contactPerson: '',
  designation: '',
  phone: '',
  email: '',
  website: '',
  billingAddress: '',
  billingCity: '',
  billingState: '',
  billingPincode: '',
  gstNumber: '',
  panNumber: '',
  creditDays: '30',
  creditLimit: '',
  agreementStart: '',
  agreementEnd: '',
  discountType: '',
  discountValue: '',
  includeTaxes: false,
  isCreditEligible: false,
  isActive: true,
  gstLegalName: '',
  defaultSettlementMode: 'GUEST',
  // A corporate's guest normally sees the rates on the statement.
  showRatesOnGuestStatement: true,
  earlyDepartureRetention: '',
  isGstRegistered: false,
  defaultSupplyType: 'STANDARD',
};

// Sections of the form (slice 3a-ii) and the fields each holds — a validation
// error badges its section and the first one is opened.
type SectionId = 'general' | 'address' | 'commercial' | 'billing';
const SECTION_OF: Record<string, SectionId> = {
  corporateCode: 'general', companyName: 'general', industry: 'general', isActive: 'general', website: 'general',
  contactPerson: 'address', designation: 'address', phone: 'address', email: 'address',
  billingAddress: 'address', billingCity: 'address', billingState: 'address', billingPincode: 'address',
  creditDays: 'commercial', creditLimit: 'commercial', agreementStart: 'commercial', agreementEnd: 'commercial',
  discountType: 'commercial', discountValue: 'commercial',
  isGstRegistered: 'billing', gstNumber: 'billing', gstLegalName: 'billing', panNumber: 'billing',
  defaultSupplyType: 'billing', defaultSettlementMode: 'billing',
};
const SECTION_TITLES: Record<SectionId, string> = {
  general: 'General', address: 'Address & Contact', commercial: 'Agreement & Credit', billing: 'Billing & Tax',
};

/** The form values of a stored corporate. */
const toFormData = (rec: Corporate, mode: 'create' | 'edit' | 'copy'): CorporateFormData => ({
  corporateCode: mode === 'copy' ? '' : (rec.corporateCode || ''),
  companyName: rec.companyName || '',
  industry: rec.industry || '',
  contactPerson: rec.contactPerson || '',
  designation: rec.designation || '',
  phone: rec.phone || '',
  email: rec.email || '',
  website: rec.website || '',
  billingAddress: rec.billingAddress || '',
  billingCity: rec.billingCity || '',
  billingState: rec.billingState || '',
  billingPincode: rec.billingPincode || '',
  gstNumber: rec.gstNumber || '',
  panNumber: rec.panNumber || '',
  creditDays: rec.creditDays != null ? String(rec.creditDays) : '',
  creditLimit: rec.creditLimit != null ? String(rec.creditLimit) : '',
  agreementStart: rec.agreementStart ? rec.agreementStart.split('T')[0] : '',
  agreementEnd: rec.agreementEnd ? rec.agreementEnd.split('T')[0] : '',
  discountType: rec.discountType || '',
  discountValue: rec.discountValue != null ? String(rec.discountValue) : '',
  includeTaxes: rec.includeTaxes ?? false,
  isCreditEligible: rec.isCreditEligible ?? false,
  gstLegalName: rec.gstLegalName || '',
  defaultSettlementMode: rec.defaultSettlementMode || 'GUEST',
  showRatesOnGuestStatement: rec.showRatesOnGuestStatement ?? true,
  earlyDepartureRetention: rec.earlyDepartureRetention ?? '',
  isGstRegistered: rec.isGstRegistered ?? !!rec.gstNumber,
  defaultSupplyType: rec.defaultSupplyType || 'STANDARD',
  isActive: rec.isActive,
});

const INDUSTRY_OPTIONS: SelectOption[] = [
  { value: 'IT', label: 'IT' },
  { value: 'Manufacturing', label: 'Manufacturing' },
  { value: 'Healthcare', label: 'Healthcare' },
  { value: 'Finance', label: 'Finance' },
  { value: 'Education', label: 'Education' },
  { value: 'Retail', label: 'Retail' },
  { value: 'Hospitality', label: 'Hospitality' },
  { value: 'Government', label: 'Government' },
  { value: 'Other', label: 'Other' },
];

const DISCOUNT_TYPE_OPTIONS: SelectOption[] = [
  { value: 'Percentage', label: 'Percentage' },
  { value: 'Flat Amount', label: 'Flat Amount' },
];

// ============================================================================
// COMPONENT
// ============================================================================

const CorporateModal: React.FC<CorporateModalProps> = ({
  isOpen, mode, record, onSave, onClose,
  tabId, moduleName, onReopen, onRestoreModal, createInstanceId, copyInstanceId, minimizeRef,
}) => {
  const { minimize } = useModalMinimize();
  // --- Internal form state (Pattern B) ---
  const [formData, setFormData] = useState<CorporateFormData>(INITIAL_FORM_DATA);
  const formDataRef = useRef(formData);
  formDataRef.current = formData;
  const modeRef = useRef(mode);
  modeRef.current = mode;
  const recordRef = useRef(record);
  recordRef.current = record;
  const createInstanceIdRef = useRef(createInstanceId);
  createInstanceIdRef.current = createInstanceId;
  const copyInstanceIdRef = useRef(copyInstanceId);
  copyInstanceIdRef.current = copyInstanceId;
  const isRestoreRef = useRef(false);
  const initialSnapshotRef = useRef('');

  // --- Validation state ---
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const getFieldError = useCallback((field: string): string | undefined => fieldErrors[field], [fieldErrors]);
  const clearFieldError = useCallback((field: string) => {
    setFieldErrors(prev => {
      if (!prev[field]) return prev;
      const next = { ...prev };
      delete next[field];
      return next;
    });
  }, []);

  // --- Confirm dialog state ---
  const [saveConfirmOpen, setSaveConfirmOpen] = useState(false);
  const [discardConfirmOpen, setDiscardConfirmOpen] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  // --- Sections (slice 3a-ii): left nav, error badges per section ---
  const [activeSection, setActiveSection] = useState<SectionId>('general');
  const { pack } = useTaxPack();

  // --- Concurrency conflict state ---
  const [concurrencyConflict, setConcurrencyConflict] = useState<{ open: boolean; message: string }>({ open: false, message: '' });
  const [isRefreshing, setIsRefreshing] = useState(false);
  const effectiveRecordRef = useRef<Corporate | null | undefined>(record);

  // --- Populate form on open ---
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!isOpen) return;
    effectiveRecordRef.current = record;
    if (isRestoreRef.current) {
      isRestoreRef.current = false;
      setFieldErrors({});
      return;
    }
    const rec = recordRef.current;
    const data: CorporateFormData = (mode === 'edit' || mode === 'copy') && rec ? toFormData(rec, mode) : { ...INITIAL_FORM_DATA };
    setFormData(data);
    initialSnapshotRef.current = JSON.stringify(data);
    setFieldErrors({});
    setActiveSection('general');
  }, [isOpen]);

  // --- Unsaved changes tracking ---
  // The Billing & Tax section edits its fields in place.
  const handleBillingChange = useCallback((patch: Partial<PartyBillingValues>) => {
    if ('gstNumber' in patch) clearFieldError('gstNumber');
    setFormData(prev => ({ ...prev, ...patch }));
  }, [clearFieldError]);

  const isDirty = useMemo(() => {
    if (!initialSnapshotRef.current) return false;
    return JSON.stringify(formData) !== initialSnapshotRef.current;
  }, [formData]);

  // --- Close handler (checks dirty state) ---
  const handleClose = useCallback(() => {
    isDirty ? setDiscardConfirmOpen(true) : onClose();
  }, [isDirty, onClose]);

  // --- Minimize handler ---
  const handleMinimize = useCallback(() => {
    if (!tabId || !moduleName) return;
    const currentFormData = formDataRef.current;
    const currentMode = modeRef.current;
    const currentCreateInstanceId = createInstanceIdRef.current;
    const currentCopyInstanceId = copyInstanceIdRef.current;
    let modalId: string;
    if (currentMode === 'create') {
      modalId = `${tabId}-form-modal-create-${currentCreateInstanceId || 'default'}`;
    } else if (currentMode === 'copy') {
      modalId = `${tabId}-form-modal-copy-${currentCopyInstanceId || 'default'}`;
    } else {
      modalId = `${tabId}-form-modal-edit-${recordRef.current?.id || 'new'}`;
    }
    let title: string;
    if (currentMode === 'create') {
      const code = currentFormData.corporateCode?.trim();
      const name = currentFormData.companyName?.trim();
      const idx = currentCreateInstanceId || '1';
      title = code ? `Add - ${code} (${idx})` : name ? `Add - ${name} (${idx})` : `Add New (${idx})`;
    } else if (currentMode === 'copy') {
      const code = currentFormData.corporateCode?.trim();
      const name = currentFormData.companyName?.trim();
      const idx = currentCopyInstanceId || '1';
      title = code ? `Copy - ${code} (${idx})` : name ? `Copy - ${name} (${idx})` : `Copy (${idx})`;
    } else {
      title = `Edit - ${currentFormData.corporateCode || currentFormData.companyName || 'Corporate'}`;
    }
    const savedFormData = { ...currentFormData };
    const savedSnapshot = initialSnapshotRef.current;
    const currentIsDirty = savedSnapshot ? JSON.stringify(currentFormData) !== savedSnapshot : false;
    minimize({
      id: modalId,
      title,
      moduleName,
      tabId,
      isDirty: currentIsDirty,
      onRestore: () => {
        isRestoreRef.current = true;
        setFormData(savedFormData);
        initialSnapshotRef.current = savedSnapshot;
        if (onRestoreModal) onRestoreModal(
          currentMode,
          currentMode === 'create' ? currentCreateInstanceId : currentMode === 'copy' ? currentCopyInstanceId : undefined,
          currentMode !== 'create' ? currentFormData.corporateCode : undefined,
        );
        else if (onReopen) onReopen();
      },
    });
    onClose();
  }, [tabId, moduleName, minimize, onClose, onRestoreModal, onReopen]);

  // Expose handleMinimize to parent for auto-minimize on new copy
  if (minimizeRef) minimizeRef.current = handleMinimize;

  // --- Validate + show save confirmation ---
  const handleSave = useCallback(async () => {
    const errors: Record<string, string> = {};

    if (!formData.corporateCode.trim()) {
      errors.corporateCode = 'Corporate Code is required';
    } else if (formData.corporateCode.length > 20) {
      errors.corporateCode = 'Corporate Code cannot exceed 20 characters';
    }

    if (!formData.companyName.trim()) {
      errors.companyName = 'Company Name is required';
    } else if (formData.companyName.length > 200) {
      errors.companyName = 'Company Name cannot exceed 200 characters';
    }

    if (!formData.contactPerson.trim()) {
      errors.contactPerson = 'Contact Person is required';
    }

    if (!formData.phone.trim()) {
      errors.phone = 'Phone is required';
    }

    if (!formData.email.trim()) {
      errors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      errors.email = 'Please enter a valid email address';
    }

    // Billing Address and the agreement dates are NOT NULL in booking.tbl_corporate.
    // They were relaxed while they sat behind a collapsed "More Details"; with the
    // sectioned layout every field is one click away, so they are required again
    // (a save without them failed in the database).
    if (!formData.billingAddress.trim()) {
      errors.billingAddress = 'Billing Address is required';
    }
    if (!formData.agreementStart) {
      errors.agreementStart = 'Agreement Start is required';
    }
    if (!formData.agreementEnd) {
      errors.agreementEnd = 'Agreement End is required';
    }

    if (formData.isGstRegistered && !formData.gstNumber.trim()) {
      errors.gstNumber = 'GSTIN is required for a registered corporate';
    }
    if (!formData.isGstRegistered && formData.billingState.trim()
      && usesStatePicker(pack, undefined) && !findState(pack, formData.billingState)) {
      errors.billingState = 'Pick the state from the list — an unregistered corporate is taxed by it';
    }

    if (formData.creditDays) {
      const val = parseInt(formData.creditDays, 10);
      if (isNaN(val) || val < 0) {
        errors.creditDays = 'Credit Days must be a positive integer';
      }
    }

    if (formData.creditLimit) {
      const val = parseFloat(formData.creditLimit);
      if (isNaN(val) || val < 0) {
        errors.creditLimit = 'Credit Limit must be a positive number';
      }
    }

    // Agreement date validation
    if (formData.agreementStart && formData.agreementEnd) {
      if (new Date(formData.agreementStart) >= new Date(formData.agreementEnd)) {
        errors.agreementEnd = 'Agreement End must be after Agreement Start';
      }
    }

    if (formData.discountValue) {
      const val = parseFloat(formData.discountValue);
      if (isNaN(val) || val < 0) {
        errors.discountValue = 'Discount Value must be a positive number';
      } else if (formData.discountType === 'Percentage' && val > 100) {
        errors.discountValue = 'Percentage discount cannot exceed 100';
      } else if (formData.discountType === 'Flat Amount' && val > 99999999.99) {
        errors.discountValue = 'Flat Amount discount cannot exceed 99,999,999.99';
      }
    }

    // Code uniqueness validation (async)
    if (!errors.corporateCode && formData.corporateCode.trim()) {
      try {
        const excludeId = mode === 'edit' ? record?.id : undefined;
        const res = await corporateService.validateCode(formData.corporateCode.trim(), excludeId);
        if (res.data === false) {
          errors.corporateCode = 'This Corporate Code already exists';
        }
      } catch {
        // Skip validation on network error
      }
    }

    if (Object.keys(errors).length > 0) {
      setFieldErrors(errors);
      const first = Object.keys(errors)[0];
      setActiveSection(SECTION_OF[first] ?? 'general');
      requestAnimationFrame(() => {
        const el = document.querySelector<HTMLElement>(`[data-field="${first}"]`);
        if (el) {
          el.scrollIntoView({ behavior: 'smooth', block: 'center' });
          el.querySelector<HTMLElement>('input')?.focus();
        }
      });
      return;
    }

    setFieldErrors({});
    setSaveConfirmOpen(true);
  }, [formData, mode, record, pack]);

  // --- Confirmed save ---
  const handleConfirmSave = useCallback(async () => {
    setSaveConfirmOpen(false);
    setIsSaving(true);

    try {
      const payload: CreateCorporateRequest | UpdateCorporateRequest = {
        corporateCode: formData.corporateCode.trim(),
        companyName: formData.companyName.trim(),
        industry: formData.industry || undefined,
        contactPerson: formData.contactPerson.trim() || undefined,
        designation: formData.designation.trim() || undefined,
        phone: formData.phone.trim() || undefined,
        email: formData.email.trim() || undefined,
        website: formData.website.trim() || undefined,
        billingAddress: formData.billingAddress.trim() || undefined,
        billingCity: formData.billingCity.trim() || undefined,
        billingState: formData.billingState.trim() || undefined,
        billingPincode: formData.billingPincode.trim() || undefined,
        // An unregistered corporate has no GSTIN (the server clears it too).
        gstNumber: formData.isGstRegistered ? formData.gstNumber.trim() || undefined : undefined,
        isGstRegistered: formData.isGstRegistered,
        defaultSupplyType: formData.defaultSupplyType,
        panNumber: formData.panNumber.trim() || undefined,
        creditDays: formData.creditDays ? parseInt(formData.creditDays, 10) : undefined,
        creditLimit: formData.creditLimit ? parseFloat(formData.creditLimit) : undefined,
        agreementStart: formData.agreementStart || undefined,
        agreementEnd: formData.agreementEnd || undefined,
        discountType: formData.discountType || undefined,
        discountValue: formData.discountValue ? parseFloat(formData.discountValue) : undefined,
        includeTaxes: formData.includeTaxes,
        isCreditEligible: formData.isCreditEligible,
        gstLegalName: formData.gstLegalName.trim() || null,
        defaultSettlementMode: formData.defaultSettlementMode,
        showRatesOnGuestStatement: formData.showRatesOnGuestStatement,
        earlyDepartureRetention: formData.earlyDepartureRetention || null,
        isActive: formData.isActive,
      };

      if (mode === 'edit') {
        const eff = effectiveRecordRef.current!;
        await corporateService.update(eff.id, { ...payload, rowVersion: eff.rowVersion } as UpdateCorporateRequest);
      } else {
        await corporateService.create(payload as CreateCorporateRequest);
      }

      setIsSaving(false);
      notificationService.toast.success(
        `"${formData.corporateCode}" ${mode === 'edit' ? 'updated' : 'created'} successfully.`
      );
      onSave();
    } catch (err: any) {
      setIsSaving(false);
      const status = err?.response?.status;
      const backendMessage =
        err?.response?.data?.message ||
        err?.response?.data?.errors?.[0] ||
        err?.message;
      if (status === 409 && backendMessage) {
        setConcurrencyConflict({ open: true, message: formatBackendTimestamps(backendMessage) });
      } else {
        notificationService.toast.error(backendMessage || 'An error occurred. Please try again.');
      }
    }
  }, [formData, mode, onSave]);

  // --- Refresh record from server after concurrency conflict ---
  const handleRefreshFromConflict = useCallback(async () => {
    const eff = effectiveRecordRef.current;
    if (!eff) {
      setConcurrencyConflict({ open: false, message: '' });
      return;
    }
    setIsRefreshing(true);
    try {
      const response = await corporateService.getById(eff.id);
      const rec = response.data;
      if (rec) {
        const data = toFormData(rec, 'edit');
        setFormData(data);
        initialSnapshotRef.current = JSON.stringify(data);
        effectiveRecordRef.current = rec;
        setFieldErrors({});
        notificationService.toast.success('Loaded the latest version. Please review and save.');
      }
      setConcurrencyConflict({ open: false, message: '' });
    } catch (err: any) {
      const backendMessage =
        err?.response?.data?.message ||
        err?.response?.data?.errors?.[0] ||
        err?.message ||
        'Failed to load the latest version.';
      notificationService.toast.error(backendMessage);
    } finally {
      setIsRefreshing(false);
    }
  }, []);

  if (!isOpen) return null;

  const err = getFieldError;
  const set = <K extends keyof CorporateFormData>(field: K, value: CorporateFormData[K]) => {
    clearFieldError(field as string);
    setFormData(prev => ({ ...prev, [field]: value }));
  };
  const errorsIn = (id: SectionId) => Object.keys(fieldErrors).filter(f => (SECTION_OF[f] ?? 'general') === id).length;
  const text = (field: keyof CorporateFormData, label: string, opts: { required?: boolean; placeholder?: string; maxLength?: number; type?: string; upper?: boolean } = {}) => (
    <div className="form-group" data-field={field}>
      <label className="form-label">{label}{opts.required && <> <span className="text-danger">*</span></>}</label>
      <input
        type={opts.type ?? 'text'}
        className={`form-input${err(field) ? ' form-field-error' : ''}`}
        value={formData[field] as string}
        onChange={(e) => set(field, (opts.upper ? e.target.value.toUpperCase() : e.target.value) as never)}
        placeholder={opts.placeholder}
        maxLength={opts.maxLength}
      />
      {err(field) && <div className="form-error-message">{err(field)}</div>}
    </div>
  );

  const sections: ShellSection[] = [
    {
      id: 'general',
      title: SECTION_TITLES.general,
      errorCount: errorsIn('general'),
      content: (
        <div className="form-grid-2">
          <div className="form-group" data-field="corporateCode">
            <label className="form-label">Corporate Code <span className="text-danger">*</span></label>
            <input
              type="text"
              className={`form-input${err('corporateCode') ? ' form-field-error' : ''}`}
              value={formData.corporateCode}
              onChange={(e) => set('corporateCode', e.target.value.toUpperCase())}
              placeholder="e.g., CORP001"
              maxLength={20}
              autoFocus={mode === 'create' || mode === 'copy'}
              disabled={mode === 'edit'}
            />
            {err('corporateCode') && <div className="form-error-message">{err('corporateCode')}</div>}
          </div>
          {text('companyName', 'Company Name', { required: true, placeholder: 'e.g., ACME Incorporated', maxLength: 200 })}
          <div className="form-group" data-field="industry">
            <SyutSelect
              label="Industry"
              options={INDUSTRY_OPTIONS}
              value={formData.industry}
              onChange={(val) => set('industry', (val as string) || '')}
              placeholder="Select industry..."
              isClearable
              size="md"
            />
          </div>
          <div className="form-group" data-field="isActive">
            <SyutSelect
              label="Status"
              options={[
                { value: 'true', label: 'Active' },
                { value: 'false', label: 'Inactive' },
              ]}
              value={String(formData.isActive)}
              onChange={(val) => set('isActive', val === 'true')}
              isClearable={false}
              size="md"
            />
          </div>
          {text('website', 'Website', { placeholder: 'e.g., https://www.company.com', maxLength: 255 })}
        </div>
      ),
    },
    {
      id: 'address',
      title: SECTION_TITLES.address,
      errorCount: errorsIn('address'),
      content: (
        <>
          <div className="form-grid-2">
            {text('contactPerson', 'Contact Person', { required: true, placeholder: 'e.g., John Smith', maxLength: 100 })}
            {text('designation', 'Designation', { placeholder: 'e.g., HR Manager', maxLength: 100 })}
            {text('phone', 'Phone', { required: true, placeholder: 'e.g., +91 9876543210', maxLength: 20 })}
            {text('email', 'Email', { required: true, placeholder: 'e.g., contact@company.com', maxLength: 255, type: 'email' })}
          </div>
          <div style={{ marginTop: '0.5rem' }}>
            {text('billingAddress', 'Billing Address', { required: true, placeholder: 'e.g., 123 Business Ave' })}
          </div>
          <div className="form-grid-3" style={{ marginTop: '0.5rem' }}>
            {text('billingCity', 'City', { placeholder: 'e.g., Bengaluru', maxLength: 100 })}
            <StateSelect
              value={formData.billingState}
              onChange={(v) => set('billingState', v)}
              pack={pack}
              field="billingState"
              error={err('billingState')}
            />
            {text('billingPincode', 'PIN Code', { placeholder: 'e.g., 560001', maxLength: 10 })}
          </div>
        </>
      ),
    },
    {
      id: 'commercial',
      title: SECTION_TITLES.commercial,
      errorCount: errorsIn('commercial'),
      content: (
        <>
          <div className="form-grid-2">
            {text('agreementStart', 'Agreement Start', { required: true, type: 'date' })}
            {text('agreementEnd', 'Agreement End', { required: true, type: 'date' })}
            {text('creditDays', 'Credit Days', { type: 'number', placeholder: '30' })}
            {text('creditLimit', 'Credit Limit', { type: 'number', placeholder: '0.00' })}
            <div className="form-group" data-field="discountType">
              <SyutSelect
                label="Discount Type"
                options={DISCOUNT_TYPE_OPTIONS}
                value={formData.discountType}
                onChange={(val) => set('discountType', (val as string) || '')}
                placeholder="Select..."
                isClearable
                size="md"
              />
            </div>
            {text('discountValue', 'Discount Value', { type: 'number', placeholder: '0.00' })}
          </div>
          <div style={{ display: 'flex', gap: '1.5rem', flexWrap: 'wrap', marginTop: '0.75rem' }}>
            <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer' }}>
              <input type="checkbox" checked={formData.includeTaxes}
                onChange={(e) => set('includeTaxes', e.target.checked)}
                style={{ width: '1rem', height: '1rem', accentColor: 'var(--primary)' }} />
              <span style={{ fontSize: '0.85rem', color: 'var(--text-primary)' }}>Tax Inclusive</span>
            </label>
            <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer' }}>
              <input type="checkbox" checked={formData.isCreditEligible}
                onChange={(e) => set('isCreditEligible', e.target.checked)}
                style={{ width: '1rem', height: '1rem', accentColor: 'var(--primary)' }} />
              <span style={{ fontSize: '0.85rem', color: 'var(--text-primary)' }}>
                Credit Eligible <span style={{ color: 'var(--text-muted)', fontSize: '0.7rem' }}>(bookings save as CONFIRMED without advance; allows credit settlement)</span>
              </span>
            </label>
          </div>
        </>
      ),
    },
    {
      id: 'billing',
      title: SECTION_TITLES.billing,
      errorCount: errorsIn('billing'),
      content: (
        <PartyBillingSection
          kind="CORPORATE"
          mode={mode}
          values={{
            isGstRegistered: formData.isGstRegistered,
            gstNumber: formData.gstNumber,
            gstLegalName: formData.gstLegalName,
            defaultSupplyType: formData.defaultSupplyType,
            defaultSettlementMode: formData.defaultSettlementMode,
            showRatesOnGuestStatement: formData.showRatesOnGuestStatement,
            earlyDepartureRetention: formData.earlyDepartureRetention,
          }}
          isCreditEligible={formData.isCreditEligible}
          onChange={handleBillingChange}
          pack={pack}
          addressState={formData.billingState}
          addressSectionTitle={SECTION_TITLES.address}
          gstError={err('gstNumber')}
          disabled={isSaving}
          extra={text('panNumber', 'PAN', { placeholder: 'e.g., AAPFU0939F', maxLength: 10, upper: true })}
        />
      ),
    },
  ];

  const blockers = (Object.keys(SECTION_TITLES) as SectionId[])
    .filter(id => errorsIn(id) > 0)
    .map(id => ({ label: `${SECTION_TITLES[id]}: ${errorsIn(id)} to fix`, sectionId: id }));

  return (
    <>
      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
      <SectionedModalShell
        title={mode === 'create' ? 'Add Corporate' : mode === 'copy' ? 'Copy Corporate' : 'Edit Corporate'}
        summary={formData.companyName || formData.corporateCode ? (
          <span style={{ fontSize: '0.85rem', color: 'var(--text-secondary)' }}>
            <strong style={{ color: 'var(--text-primary)' }}>{formData.companyName || '—'}</strong>
            {formData.corporateCode && <> · {formData.corporateCode}</>}
            {formData.isGstRegistered && formData.gstNumber && <> · GSTIN {formData.gstNumber}</>}
          </span>
        ) : undefined}
        sections={sections}
        activeSection={activeSection}
        onSectionChange={(id) => setActiveSection(id as SectionId)}
        onClose={handleClose}
        onMinimize={tabId ? handleMinimize : undefined}
        busy={isSaving}
        loading={{ active: isSaving, label: mode === 'edit' ? 'Saving...' : 'Creating...' }}
        maxWidth="960px"
        footer={
          <>
            <ShellBlockers blockers={blockers} onJump={(id) => setActiveSection(id as SectionId)} />
            <button className="btn btn-secondary" onClick={handleClose} disabled={isSaving}>Cancel</button>
            <button
              className="btn btn-primary"
              onClick={handleSave}
              disabled={isSaving || (mode === 'edit' && !isDirty)}
              title={mode === 'edit' && !isDirty ? 'No changes to save' : undefined}
            >
              <IconSystem.save size={16} />
              {mode === 'edit' ? 'Save Changes' : 'Create Corporate'}
            </button>
          </>
        }
      />
      {/* Save confirmation */}
      <ConfirmDialog
        open={saveConfirmOpen}
        title={mode === 'edit' ? 'Confirm Update' : 'Confirm Create'}
        message={`Are you sure you want to ${mode === 'edit' ? 'update' : 'create'} the corporate "${formData.corporateCode}"?`}
        confirmLabel={mode === 'edit' ? 'Save' : 'Create'}
        cancelLabel="Cancel"
        variant="default"
        onConfirm={handleConfirmSave}
        onCancel={() => setSaveConfirmOpen(false)}
      />

      {/* Discard changes confirmation */}
      <ConfirmDialog
        open={discardConfirmOpen}
        title="Discard Changes?"
        message="You have unsaved changes. Do you want to discard them?"
        confirmLabel="Discard"
        cancelLabel="Keep Editing"
        variant="danger"
        onConfirm={() => { setDiscardConfirmOpen(false); onClose(); }}
        onCancel={() => setDiscardConfirmOpen(false)}
      />

      {/* Concurrency conflict — refresh from server */}
      <ConfirmDialog
        open={concurrencyConflict.open}
        title="Record Modified by Another User"
        message={concurrencyConflict.message}
        confirmLabel="Load Latest"
        cancelLabel="Keep My Changes"
        variant="default"
        loading={isRefreshing}
        loadingMessage="Loading latest version..."
        onConfirm={handleRefreshFromConflict}
        onCancel={() => setConcurrencyConflict({ open: false, message: '' })}
      />
    </>
  );
};

export default CorporateModal;
